import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { usePrimaryPage, useSecondaryPage } from '@/PageManager'
import { toTheme } from '@/lib/link'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Key01Icon,
  Login01Icon,
  Logout01Icon,
  SwatchIcon,
  UserIcon
} from '@hugeicons/core-free-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SidebarItem from './SidebarItem'
import { accountAtom, loginDialogOpenAtom, useCurrentPubkey } from '@/atoms/session'
import { pomegranateAuthAtom } from '@/atoms/pomegranate'
import { useAtomValue, useSetAtom } from 'jotai'
import { SimpleUserAvatar } from '../UserAvatar'
import SimpleUsername from '../SimpleUsername'
import LogoutDialog from '../LogoutDialog'

export default function AccountButton({ collapse }: { collapse: boolean }) {
  const pubkey = useCurrentPubkey()
  if (pubkey) {
    return <ProfileButton collapse={collapse} />
  } else {
    return <LoginButton collapse={collapse} />
  }
}

function ProfileButton({ collapse }: { collapse: boolean }) {
  const { t } = useTranslation()
  const account = useAtomValue(accountAtom)
  const pomegranateAuth = useAtomValue(pomegranateAuthAtom)
  const { navigate } = usePrimaryPage()
  const { push } = useSecondaryPage()
  const pubkey = account?.pubkey
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  if (!pubkey) return null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30 shadow-none p-2 flex items-center bg-transparent text-foreground hover:text-accent-foreground rounded-lg justify-start gap-4 text-lg font-semibold',
              collapse ? 'w-12 h-12' : 'w-full h-auto'
            )}
          >
            <div className="flex gap-2 items-center flex-1 w-0">
              <SimpleUserAvatar size="medium" user={pubkey} />
              {!collapse && (
                <SimpleUsername className="truncate font-semibold text-lg" user={pubkey} />
              )}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" className="min-w-42">
          <DropdownMenuItem onClick={() => navigate('profile')}>
            <HugeiconsIcon icon={UserIcon} />
            <span className="shrink-0">{t('Profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => push(toTheme())}>
            <HugeiconsIcon icon={SwatchIcon} />
            <span className="shrink-0">{t('Theme')}</span>
          </DropdownMenuItem>
          {!!pomegranateAuth && (
            <DropdownMenuItem onClick={() => push('/pomegranate')}>
              <HugeiconsIcon icon={Key01Icon} />
              <span className="shrink-0">{t('Pomegranate')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setLogoutDialogOpen(true)}
          >
            <HugeiconsIcon icon={Logout01Icon} />
            <span className="shrink-0">{t('Logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LogoutDialog open={logoutDialogOpen} setOpen={setLogoutDialogOpen} />
    </>
  )
}

function LoginButton({ collapse }: { collapse: boolean }) {
  const setLoginDialogOpen = useSetAtom(loginDialogOpenAtom)

  return (
    <SidebarItem onClick={() => setLoginDialogOpen(true)} title="Login" collapse={collapse}>
      <HugeiconsIcon icon={Login01Icon} />
    </SidebarItem>
  )
}
